import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Build Your Look: one styled outfit, sold as a set of real products.
 *
 * The look's landing page is an ordinary product, but it is never the thing that
 * goes in the cart. What goes in the cart are the component products listed on the
 * parent's `custom.look_items` metafield, each with its own variants, stock and
 * price. This element owns the part of that which cannot be rendered ahead of time:
 * which pieces are still included, which variant each one resolves to, what the
 * running total is, and the single request that adds them all at once.
 *
 * The total is a real sum of real variant prices, stepped down by the same
 * progressive discount the cart applies: the more pieces of the look are included,
 * the larger the step, and taking every piece is the largest step of all. Nothing
 * here is invented in the browser. The ladder arrives on `data-ladder` and
 * `data-full-discount`, rendered from the block, which mirrors the automatic
 * discounts `automation/setup_look_discounts.py` creates. A total the checkout will
 * not honour is worse than no total at all, so `#discountFor` is the single place
 * that decides what the shopper is told she will pay.
 *
 * Looks carried a free gift card until 2026-10-24, when the gift moved to the
 * welcome code (`docs/welcome-gift.md`). The `[data-look-gift]` handling below is
 * dormant: no look renders that card any more, and it returns early without one.
 *
 * @extends {Component}
 */
class ShopTheLookComponent extends Component {
  requiredRefs = ['addButton', 'summaryCount', 'summaryTotal', 'notice'];

  /**
   * @typedef {object} LookVariant
   * @property {number} id - Variant id.
   * @property {string[]} o - Option values, in option position order.
   * @property {number} p - Price in minor units.
   * @property {number} c - Compare-at price in minor units, or 0 when there is none.
   * @property {boolean} a - Whether the variant can be bought right now.
   * @property {string} [img] - Variant image, when it has one of its own.
   */

  /**
   * @typedef {object} LookItem
   * @property {HTMLElement} el - The card.
   * @property {LookVariant[]} variants - Every variant of the component product.
   * @property {string[]} selection - Currently chosen option value per position.
   * @property {HTMLInputElement} toggle - The include/remove control.
   */

  /** @type {LookItem[]} */
  #items = [];

  /** @type {{ el: HTMLElement, toggle: HTMLInputElement } | null} */
  #gift = null;

  /** Guards the add request so a second press cannot start a second cart mutation. */
  #busy = false;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** Whether the shopper has reached the builder yet. */
  #reached = false;

  /** Whether the inline call to action is on screen. */
  #ctaVisible = true;

  /** @type {IntersectionObserver | undefined} */
  #footerObserver;

  /** Whether the footer, or the 200px above it, is on screen. */
  #footerVisible = false;

  /** Whether the inline call to action has scrolled away above the viewport. */
  #ctaPassed = false;

  /** Detaches listeners that outlive a single observer callback. */
  #abort = new AbortController();

  /** @type {number | undefined} */
  #noticeTimeout;

  /**
   * The progressive discount, largest minimum first so the first match wins.
   * @type {{ minimum: number, percent: number }[]}
   */
  #ladder = [];

  /** Percent off when every piece of the look is included. */
  #fullDiscount = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#readLadder();
    this.#collect();
    this.#render();
    this.#watchScroll();
  }

  /**
   * The theme editor re-renders sections in place, which can replace every card
   * under this element while leaving the element itself mounted.
   */
  updatedCallback() {
    super.updatedCallback?.();
    this.#collect();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#footerObserver?.disconnect();
    this.#abort.abort();
    clearTimeout(this.#noticeTimeout);
  }

  /** Reads the cards back out of the DOM into the state this element works on. */
  #collect() {
    const cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-look-item]')]);

    this.#items = cards.flatMap((el) => {
      const data = el.querySelector('script[data-look-variants]')?.textContent;
      const toggle = el.querySelector('[data-look-toggle]');

      if (!data || !(toggle instanceof HTMLInputElement)) return [];

      /** @type {LookVariant[]} */
      let variants;
      try {
        variants = JSON.parse(data);
      } catch {
        return [];
      }

      /** @type {string[]} */
      let selection;
      try {
        selection = JSON.parse(el.dataset.lookOptions || '[]');
      } catch {
        selection = [];
      }

      // Controls only exist for options that actually offer a choice. The rest keep
      // the single value the server rendered into `data-look-options`.
      for (const control of el.querySelectorAll('[data-option-index]')) {
        const index = Number(/** @type {HTMLElement} */ (control).dataset.optionIndex);
        if (control instanceof HTMLInputElement && control.checked) selection[index] = control.value;
        if (control instanceof HTMLSelectElement) selection[index] = control.value;
      }

      return [{ el, variants, selection, toggle }];
    });

    const giftEl = this.querySelector('[data-look-gift]');
    const giftToggle = giftEl?.querySelector('[data-look-gift-toggle]');
    this.#gift =
      giftEl instanceof HTMLElement && giftToggle instanceof HTMLInputElement ? { el: giftEl, toggle: giftToggle } : null;
  }

  /**
   * Routes every change inside the builder. One listener on the root rather than one
   * per control, so cards can be added or removed without rewiring anything.
   *
   * @param {Event} event
   */
  handleChange(event) {
    // The theme hands `on:` handlers the element carrying the attribute as
    // `event.target`, which here is this element rather than the control that
    // changed. The composed path still starts at the real one.
    const target = event.composedPath?.()[0] ?? event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.closest('[data-look-gift]')) {
      this.#paintGift(target);
      return;
    }

    const card = target.closest('[data-look-item]');
    const item = this.#items.find((candidate) => candidate.el === card);
    if (!item) return;

    if (target === item.toggle) {
      this.#render();
      return;
    }

    const index = Number(target.dataset.optionIndex);
    if (Number.isNaN(index)) return;

    if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) {
      item.selection[index] = target.value;

      // Keep the picker's own selected/unselected attributes in step, since the
      // theme's variant styles animate from them rather than from `:checked` alone.
      if (target instanceof HTMLInputElement) {
        for (const sibling of item.el.querySelectorAll(`input[name="${CSS.escape(target.name)}"]`)) {
          /** @type {HTMLElement} */ (sibling).dataset.currentChecked = String(sibling === target);
        }

        // The swatch legend names the chosen colour. The product page gets that from
        // a section re-render; here nothing re-renders, so the text follows by hand.
        const label = target.closest('fieldset')?.querySelector('.variant-option__swatch-value');
        if (label) label.textContent = target.value;
      }

      this.#render();
    }
  }

  /**
   * Keeps the gift card in step with its own controls. The gift is never part of the
   * total, so this touches nothing outside the card.
   *
   * @param {HTMLElement} target
   */
  #paintGift(target) {
    const gift = this.#gift;
    if (!gift) return;

    gift.el.dataset.included = String(gift.toggle.checked);

    if (target instanceof HTMLInputElement && target.matches('[data-look-gift-variant]')) {
      for (const sibling of gift.el.querySelectorAll('[data-look-gift-variant]')) {
        /** @type {HTMLElement} */ (sibling).dataset.currentChecked = String(sibling === target);
      }

      const image = gift.el.querySelector('[data-look-gallery-trigger] img');
      const src = target.dataset.img;
      if (image instanceof HTMLImageElement && src && image.src !== src) {
        image.src = src;
        image.removeAttribute('srcset');
      }
    }
  }

  /**
   * Adds the gift to the pieces about to go in, when its card is on and the cart does
   * not already hold it, and records a switched off card as a decline.
   *
   * @param {{ id: number, quantity: number, properties?: Record<string, string> }[]} lines
   */
  async #prepareGift(lines) {
    const gift = this.#gift;
    if (!gift) return;

    const chosen = gift.el.querySelector('[data-look-gift-variant]:checked, input[type="hidden"][data-look-gift-variant]');
    const variantId = chosen instanceof HTMLInputElement ? Number(chosen.value) : NaN;
    if (!Number.isFinite(variantId)) return;

    const cart = await this.#readCart();
    const productId = gift.el.dataset.productId;
    const inCart = (cart.items || []).some((item) => String(item.product_id) === productId);

    if (gift.toggle.checked && !inCart) lines.push({ id: variantId, quantity: 1 });

    const declined = gift.toggle.checked ? '' : '1';
    if ((cart.attributes?._look_gift_declined || '') !== declined) {
      await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _look_gift_declined: declined } }),
      });
    }
  }

  /**
   * The variant a card currently resolves to, or null when the chosen combination
   * does not exist as a variant at all.
   *
   * @param {LookItem} item
   * @returns {LookVariant | null}
   */
  #resolve(item) {
    return item.variants.find((variant) => variant.o.every((value, i) => value === item.selection[i])) ?? null;
  }

  /**
   * Reads `2:3,3:6,4:9` into rungs, sorted so the richest match is found first.
   *
   * A malformed pair is dropped rather than defaulted, because a rung invented
   * here would be a discount the cart never heard of. An empty ladder simply
   * means no step is ever applied, and the total falls back to the plain sum.
   */
  #readLadder() {
    this.#ladder = (this.dataset.ladder || '')
      .split(',')
      .map((pair) => pair.split(':').map(Number))
      .filter(([minimum, percent]) => Number.isFinite(minimum) && Number.isFinite(percent) && minimum > 0)
      .map(([minimum, percent]) => ({ minimum, percent }))
      .sort((a, b) => b.minimum - a.minimum);

    const full = Number(this.dataset.fullDiscount);
    this.#fullDiscount = Number.isFinite(full) ? full : 0;
  }

  /**
   * Percent off for a basket of `count` pieces out of `size` in the look.
   *
   * Taking everything is its own step, not the top of the ladder, and it is
   * deliberately far enough above the rung below it that completing the look
   * lowers the total rather than raising it. `setup_look_discounts.py --check`
   * is what holds that property true as prices move.
   */
  #discountFor(count, size) {
    if (size > 0 && count >= size) return this.#fullDiscount;
    return this.#ladder.find((rung) => count >= rung.minimum)?.percent ?? 0;
  }

  /** Repaints every card, the summary, and both calls to action from current state. */
  #render() {
    let count = 0;
    let total = 0;
    let compareTotal = 0;
    let blocked = false;

    for (const item of this.#items) {
      const variant = this.#resolve(item);
      const included = item.toggle.checked;

      item.el.dataset.included = String(included);
      this.#paintOptions(item);
      this.#paintCard(item, variant);

      if (!included) continue;

      // A piece that is included but cannot be bought is the one state where the
      // total would be a lie, so it stops the whole look rather than quietly
      // dropping itself out of the sum. It is left out of the count as well as
      // out of the money, so the two never contradict each other, and the notice
      // under the button says what happened.
      if (!variant || !variant.a) {
        blocked = true;
        continue;
      }

      count += 1;
      total += variant.p;
      compareTotal += variant.c > variant.p ? variant.c : variant.p;
    }

    // The struck total keeps every piece at its own undiscounted price, so the
    // saving shown covers both a piece already on sale and the step earned by
    // taking more of the look. Prices are in minor units, and the cart rounds
    // the same way, so the two agree to the penny.
    const percent = this.#discountFor(count, this.#items.length);
    total = Math.round((total * (100 - percent)) / 100);

    this.#paintSummary({ count, total, compareTotal, blocked });
  }

  /**
   * Marks each option value with whether it leads anywhere, so a size that is sold
   * out reads as sold out before it is pressed.
   *
   * @param {LookItem} item
   */
  #paintOptions(item) {
    for (const control of item.el.querySelectorAll('[data-option-index]')) {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) continue;

      const index = Number(control.dataset.optionIndex);

      /** @param {string} value */
      const buyable = (value) =>
        item.variants.some(
          (variant) =>
            variant.a &&
            variant.o[index] === value &&
            variant.o.every((option, i) => i === index || option === item.selection[i])
        );

      if (control instanceof HTMLInputElement) {
        const available = buyable(control.value);
        control.dataset.optionAvailable = String(available);
        control.setAttribute('aria-disabled', String(!available));
      } else {
        const suffix = this.dataset.unavailableSuffix || '';

        for (const option of control.options) {
          const available = buyable(option.value);
          const label = option.dataset.label || option.value;

          option.dataset.optionAvailable = String(available);
          option.textContent = available ? label : `${label}${suffix}`;
        }
      }
    }
  }

  /**
   * @param {LookItem} item
   * @param {LookVariant | null} variant
   */
  #paintCard(item, variant) {
    const price = item.el.querySelector('[data-look-price]');
    const compare = item.el.querySelector('[data-look-compare]');
    const note = item.el.querySelector('[data-look-availability]');
    const image = item.el.querySelector('[data-look-image]');

    if (price) price.textContent = variant ? this.#money(variant.p) : '';

    if (compare instanceof HTMLElement) {
      const show = Boolean(variant && variant.c > variant.p);
      compare.textContent = show && variant ? this.#money(variant.c) : '';
      compare.hidden = !show;
    }

    if (note instanceof HTMLElement) {
      note.hidden = Boolean(variant?.a);
      if (!variant) note.textContent = note.dataset.unavailableText || '';
      else if (!variant.a) note.textContent = note.dataset.soldOutText || '';
    }

    if (image instanceof HTMLImageElement && variant?.img && image.src !== variant.img) {
      image.src = variant.img;
      image.removeAttribute('srcset');
    }
  }

  /**
   * Paints what the shopper is told the look costs.
   *
   * `total` arrives already stepped down by `#discountFor`, and `compareTotal`
   * holds every piece at its own undiscounted price, so the saving between them
   * is both halves at once: whatever a piece already carries in its compare-at,
   * plus the step earned by taking more of the look.
   *
   * @param {{count: number, total: number, compareTotal: number, blocked: boolean}} state
   */
  #paintSummary({ count, total, compareTotal, blocked }) {
    const { summaryCount, summaryTotal, summaryCompare, summaryPercent, summarySaving, notice, addButton } = this.refs;
    const singular = this.dataset.itemLabelSingular || 'item selected';
    const plural = this.dataset.itemLabelPlural || 'items selected';

    summaryCount.textContent = `${count} ${count === 1 ? singular : plural}`;
    summaryTotal.textContent = this.#money(total);

    const saving = compareTotal - total;

    if (summaryCompare instanceof HTMLElement) {
      summaryCompare.textContent = saving > 0 ? this.#money(compareTotal) : '';
      summaryCompare.hidden = saving <= 0;
    }

    if (summaryPercent instanceof HTMLElement) {
      const percent = saving > 0 ? Math.round((saving / compareTotal) * 100) : 0;
      summaryPercent.textContent = percent > 0 ? `-${percent}%` : '';
      summaryPercent.hidden = percent <= 0;
    }

    if (summarySaving instanceof HTMLElement) {
      const template = summarySaving.dataset.savingText || 'You save [amount]';
      summarySaving.textContent = saving > 0 ? template.replace('[amount]', this.#money(saving)) : '';
      summarySaving.hidden = saving <= 0;
    }

    const message = blocked ? this.dataset.blockedNotice : count === 0 ? this.dataset.emptyNotice : '';

    if (notice instanceof HTMLElement) {
      notice.textContent = message || '';
      notice.hidden = !message;
    }

    const disabled = this.#busy || count === 0 || blocked;
    for (const button of this.#buttons) {
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    }

    const { stickyCount, stickyTotal, stickyCompare } = this.refs;
    if (stickyCount instanceof HTMLElement) stickyCount.textContent = summaryCount.textContent;
    if (stickyTotal instanceof HTMLElement) stickyTotal.textContent = summaryTotal.textContent;

    if (stickyCompare instanceof HTMLElement) {
      stickyCompare.textContent = saving > 0 ? this.#money(compareTotal) : '';
      stickyCompare.hidden = saving <= 0;
    }

    addButton.closest('[data-look-cta]')?.setAttribute('data-count', String(count));
  }

  /** @returns {HTMLButtonElement[]} */
  get #buttons() {
    const { addButton, stickyButton } = this.refs;
    return [addButton, stickyButton].filter(
      /** @returns {button is HTMLButtonElement} */ (button) => button instanceof HTMLButtonElement
    );
  }

  /**
   * @param {number} value - Amount in minor units.
   * @returns {string}
   */
  #money(value) {
    const template = this.refs.moneyFormat;
    const format = template instanceof HTMLTemplateElement ? template.content.textContent?.trim() : null;

    return formatMoney(value, format || '{{amount}}', this.dataset.currency || '');
  }

  /**
   * Adds every included piece in one request.
   *
   * The event that opens the cart drawer is dispatched only after the cart has
   * actually changed. `/cart/add.js` is all-or-nothing for a batch, so a failure
   * means nothing was added, and opening the drawer on it would show a cart that
   * quietly contradicts the button the shopper just pressed.
   */
  addLook = async () => {
    if (this.#busy) return;

    const lines = [];

    for (const item of this.#items) {
      if (!item.toggle.checked) continue;

      const variant = this.#resolve(item);
      if (!variant?.a) return this.#render();

      /** @type {Record<string, string>} */
      const properties = {};
      if (this.dataset.lookTitle) properties._look = this.dataset.lookTitle;
      if (this.dataset.lookId) properties._look_id = this.dataset.lookId;

      lines.push({ id: variant.id, quantity: 1, properties });
    }

    if (lines.length === 0) return this.#render();

    this.#setBusy(true);

    const sections = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) sections.add(component.dataset.sectionId);
    }

    try {
      await this.#prepareGift(lines);

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lines,
          sections: [...sections].join(','),
          sections_url: window.location.pathname,
        }),
      }).then((result) => result.json());

      if (response.status) {
        throw new Error(response.description || response.message || this.dataset.errorNotice);
      }

      const cart = await this.#readCart();
      const deferred = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
          promise: deferred.promise,
        })
      );

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          source: 'shop-the-look-component',
          items: cart.items,
          itemCount: lines.length,
          productId: this.dataset.lookId,
          sections: response.sections,
          didError: false,
        },
      });

      // Both of these repaint from state, so the message has to be written after
      // them: `#setBusy(false)` re-renders, and a re-render is what clears the
      // notice back to whatever the current selection says.
      this.#setBusy(false);
      this.#celebrate();
    } catch (error) {
      this.#setBusy(false);
      this.#announce(error instanceof Error ? error.message : this.dataset.errorNotice || 'Add to cart failed');

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Add to cart failed',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  };

  async #readCart() {
    return fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('data-busy', busy);

    for (const button of this.#buttons) {
      button.setAttribute('aria-busy', String(busy));
    }

    // Re-derive the disabled state rather than restoring it, since zero selected
    // and a sold out piece both outlive the request.
    if (!busy) this.#render();
    else for (const button of this.#buttons) button.disabled = true;
  }

  /** Borrows the theme's add-to-cart checkmark, which is driven purely by the attribute. */
  #celebrate() {
    for (const button of this.#buttons) {
      button.dataset.added = 'true';
      setTimeout(() => delete button.dataset.added, 2000);
    }

    this.#announce(this.dataset.addedNotice || '');

    // The confirmation is not a state, it is an event. Any later change repaints
    // over it anyway; this is for the shopper who does nothing after adding.
    this.#noticeTimeout = setTimeout(() => this.#render(), 5000);
  }

  /** @param {string} message */
  #announce(message) {
    const { notice } = this.refs;
    if (!(notice instanceof HTMLElement)) return;

    // Whatever was about to wipe the last message must not wipe this one.
    clearTimeout(this.#noticeTimeout);

    notice.textContent = message;
    notice.hidden = !message;
  }

  /**
   * Reveals the sticky bar once the builder has been reached and its own call to
   * action has scrolled away. Kept off until then so it never covers the gallery
   * on the way down.
   *
   * The rest mirrors the product page's bar in `sticky-add-to-cart.js`: it steps
   * aside when the footer comes up, and stays away entirely while Shopify Inbox
   * chat is on the page, since both sit on the bottom edge.
   */
  #watchScroll() {
    const cta = this.querySelector('[data-look-cta]');
    const { stickyBar } = this.refs;
    if (!cta || !(stickyBar instanceof HTMLElement) || this.dataset.sticky !== 'true') return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    const desktop = window.matchMedia('(min-width: 750px)');

    const update = () => {
      const chat = Boolean(customElements.get('shopify-chat') && document.querySelector('shopify-chat'));
      // On a phone the builder is a long scroll of its own, so the bar keeps the
      // total in view while she works down the pieces. On a desktop the builder
      // is on screen from the first paint with its button just below the fold,
      // and the bar would pop up over the gallery before she has scrolled at
      // all; there it waits until the button has gone past, as the product
      // page's bar does.
      const due = desktop.matches ? this.#ctaPassed : this.#reached;
      const visible = due && !this.#ctaVisible && !this.#footerVisible && !chat;

      stickyBar.dataset.stuck = String(visible);
      // Faded and pushed off screen is not the same as gone: without this the
      // hidden button still takes a Tab stop on its way through the page.
      stickyBar.inert = !visible;
      this.toggleAttribute('data-sticky-visible', visible);
    };

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === this) {
          this.#reached = entry.isIntersecting || entry.boundingClientRect.top < 0;
        } else if (entry.target === cta) {
          this.#ctaVisible = entry.isIntersecting;
          this.#ctaPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        }
      }

      update();
    });

    desktop.addEventListener('change', update, { signal: this.#abort.signal });

    this.#observer.observe(this);
    this.#observer.observe(cta);

    if (footer) {
      // The same 200px head start the product page's bar gives itself, so it is
      // already on its way out before the footer's first line is readable.
      this.#footerObserver = new IntersectionObserver(
        ([entry]) => {
          this.#footerVisible = Boolean(entry?.isIntersecting);
          update();
        },
        { rootMargin: '200px 0px 0px 0px' }
      );
      this.#footerObserver.observe(footer);
    }

    customElements.whenDefined('shopify-chat').then(() => this.isConnected && update());
  }
}

if (!customElements.get('shop-the-look-component')) {
  customElements.define('shop-the-look-component', ShopTheLookComponent);
}
